import { useEffect, useMemo } from 'react';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { keyBy } from 'lodash';

import { useQueryParams } from '@/hooks/router/use-query-params';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { useNow } from '@/hooks/useNow';

import { ListTops } from '../api/queries/list-tops';
import { ListUnits } from '../api/queries/list-units';
import { ChartType } from '../entities/chart-type';
import type { Filters } from '../entities/filters';
import type { Top } from '../entities/top';
import type { Unit } from '../entities/unit';

import { DATA_EXPLORER_QUERY_PARAMS } from './constants';
import { useDefaultFilters } from './use-default-filters';
import { useDevice } from './use-device';

export function useFilters(): [Filters, (updated: Partial<Filters>) => void] {
  const {
    config: { defaultDataExplorerFilters: defaultFilters },
  } = useMe();
  const now = useNow();

  const [
    {
      deviceId,
      topId,
      unitIds,
      chartTypes = Object.values(ChartType),
      from = addDays(now, -defaultFilters.timeRange),
      to = now,
      filterNoise = true,
    },
    queryParams,
  ] = useQueryParams(DATA_EXPLORER_QUERY_PARAMS);

  const currentAsset = useCurrentAsset();
  const listTops = ListTops.useQuery({ currentAsset }, { retry: 3 });
  const { vehicle } = currentAsset;

  const top = useMemo<Top | undefined>(() => {
    if (vehicle) {
      return listTops.data?.tops.find((top) => top.id === vehicle.id);
    }

    if (topId) {
      return listTops.data?.tops.find((top) => top.id === topId);
    }

    return undefined;
  }, [topId, listTops.data, vehicle]);

  const listUnits = ListUnits.useQuery(
    { top },
    { retry: 3, keepPreviousData: true },
  );

  const { selectDeviceUnit, selectDeviceTop } = useDevice({
    listTops,
    listUnits,
  });

  const { selectFirstUnit, selectFirstTop } = useDefaultFilters({
    listTops,
    listUnits,
  });

  const units = useMemo<Unit[] | undefined>(
    function () {
      if (!unitIds) {
        return undefined;
      }

      const unitPerId = keyBy(listUnits?.data?.units, 'id');

      return unitIds.map((unitId) => unitPerId[unitId]).filter(Boolean);
    },
    [unitIds, listUnits.data],
  );

  useEffect(() => {
    if (deviceId) {
      if (!topId) {
        selectDeviceTop();
      } else if (!unitIds?.length) {
        selectDeviceUnit();
      }
    } else {
      if (!topId) {
        selectFirstTop();
      } else if (!unitIds?.length) {
        selectFirstUnit();
      }
    }
  }, [
    deviceId,
    topId,
    unitIds,
    selectFirstUnit,
    selectFirstTop,
    selectDeviceTop,
    selectDeviceUnit,
  ]);

  const filters: Filters = {
    deviceId,
    topId,
    top,
    unitIds,
    units,
    chartTypes,
    from: startOfDay(from),
    to: endOfDay(to),
    filterNoise,
  };

  return [
    filters,
    function update(filters) {
      if ('top' in filters) {
        filters.topId = filters.top?.id;
        delete filters.top;
      }

      if ('units' in filters) {
        filters.unitIds = filters.units?.map((unit) => unit.id) ?? [];
        delete filters.units;

        if (filters.unitIds.length === 0) {
          filters.unitIds = undefined;
        }
      }

      queryParams.replace(filters);
    },
  ];
}
